import { formatIsoDateOnly } from '../../../utils/dateUtils';
import { ALL_CATEGORY_LABELS } from '../unbilledCharges/categories';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import api from '../../../services/api';
import { ViewedProsProvider } from '../../../contexts/ViewedProsContext';
import DrilldownModal from '../unbilledCharges/DrilldownModal';
import type { MatrixCellRef } from '../unbilledCharges/MatrixTable';
import type { UnbilledChargeRow } from '../unbilledCharges/types';
import { reportError } from '../../accounting/reports/reportUtils';

const noop = () => {};
const currency = (value: unknown) => Number(value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function UnbilledDrilldown({ cell, start, end, onClose }: {
  cell: MatrixCellRef; start: string; end: string; onClose: () => void;
}) {
  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const query = useQuery({
    queryKey: ['executive-unbilled-detail', start, end, cell.axisValue, cell.subValue, cell.category],
    queryFn: async ({ signal }) => (await api.get<{ rows: UnbilledChargeRow[] }>('/reporting/executive-report/unbilled-detail', {
      params: { start, end, axis_value: cell.axisValue, ...(cell.subValue !== undefined ? { sub_value: cell.subValue } : {}), ...(cell.category ? { category: cell.category } : {}) }, signal,
    })).data.rows,
    retry: false,
  });
  const text = (key: keyof UnbilledChargeRow, header: string): ColumnDef<UnbilledChargeRow> => ({ accessorKey: key, header });
  const amount = (key: 'cost' | 'revenue' | 'margin', header: string, sign = 1): ColumnDef<UnbilledChargeRow> => ({
    id: key, header, accessorFn: row => sign * row[key], cell: context => currency(context.getValue()),
    footer: context => currency(context.table.getFilteredRowModel().rows.reduce((sum, row) => sum + sign * row.original[key], 0)),
  });
  const columns: ColumnDef<UnbilledChargeRow>[] = [
    { accessorKey: 'pronumber', header: 'PRO #', cell: ({ row }) => <button className="text-blue-600 underline" onClick={() => setSelectedPro(Number(row.original.pronumber))}>{row.original.pronumber}</button> },
    { id: 'category', header: 'Category', accessorFn: row => ALL_CATEGORY_LABELS[row.category] ?? row.category }, text('container', 'Equipment #'), { accessorKey: 'delivery_date', header: 'Delivered', cell: context => formatIsoDateOnly(context.getValue<string>()) },
    text('customer', 'Customer'), text('billto', 'Bill To'), text('region', 'Region'),
    text('responsible_party', 'Responsible Party'), text('csr_name', 'LC'), text('dispatcher', 'Dispatcher'),
    amount('cost', 'Cost / Missing Charge'), amount('revenue', 'Billed Revenue'), amount('margin', 'Unbilled Amount', -1),
    text('lc_reason', 'LC Reason'), text('mgr_comment', 'Comments'),
  ];
  return <ViewedProsProvider><DrilldownModal readOnly settingsKey="executive-unbilled-drilldown"
    cell={cell} context={`Delivered ${formatIsoDateOnly(start)} – ${formatIsoDateOnly(end)} · GWI dispatchers`} rows={query.data ?? []}
    loading={query.isFetching} error={query.isError ? reportError(query.error, 'Unable to load unbilled charges.') : null} columns={columns}
    availableFields={[]} additionalFields={[]} onAdditionalFieldsChange={noop}
    getRowId={row => `${row.pronumber}-${row.category}-${row.groupresponsible}-${row.responsible_party}`}
    onBulkEdit={noop} pendingCount={0} saving={false} onSave={noop} onDiscard={noop}
    selectedPro={selectedPro} isProDrawerOpen={selectedPro !== null} onCloseProDrawer={() => setSelectedPro(null)} onClose={onClose}
  /></ViewedProsProvider>;
}
